import { fetchMfaStatus, useAuthentication, useQueryParam, validateRedirect } from '@psyomics/ui-common';
import { useEffect, useMemo, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';

import { useGetServiceUserQuery } from '../../../features/account/hooks/use-get-service-user-query';
import { PATIENT_REDIRECT_WHITELIST } from '../../redirect-whitelists';

type MfaStatus = {
  mfaIsComplete: boolean | null;
  hasPhoneNumber: boolean | null;
  error: Error | null;
  isLoadingMfaStatus: boolean;
  hasResolved: boolean;
};

const useMfaStatus = ({
  apiUrl,
  isAuthenticated,
  isLoadingAuth,
  isMfaSetupRoute,
}: {
  apiUrl: string;
  isAuthenticated: boolean;
  isLoadingAuth: boolean;
  isMfaSetupRoute: boolean;
}): MfaStatus => {
  const [mfaIsComplete, setMfaIsComplete] = useState<boolean | null>(null);
  const [hasPhoneNumber, setHasPhoneNumber] = useState<boolean | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [isLoadingMfaStatus, setIsLoadingMfaStatus] = useState(true);
  const [hasResolved, setHasResolved] = useState(false);

  useEffect(() => {
    if (!isAuthenticated || isLoadingAuth) return;

    const isInitialFetch = !hasResolved;
    setIsLoadingMfaStatus(isInitialFetch);

    setError(null);

    fetchMfaStatus(apiUrl)
      .then((response) => {
        setHasPhoneNumber(response.hasPhoneNumber);
        setMfaIsComplete(response.mfaComplete);
        setHasResolved(true);
        setIsLoadingMfaStatus(false);
      })
      .catch((err) => {
        setError(new Error(String(err)));
        setIsLoadingMfaStatus(false);
      });
  }, [apiUrl, hasResolved, isAuthenticated, isLoadingAuth, isMfaSetupRoute]);

  return {
    error,
    hasPhoneNumber,
    hasResolved,
    isLoadingMfaStatus,
    mfaIsComplete,
  };
};

const getSetupUrl = (hasPhoneNumber: boolean, redirect: string) => {
  const params = new URLSearchParams({ redirect });
  if (!hasPhoneNumber) params.append('require-phone', '');
  return `/mfa-setup?${params.toString()}`;
};

const getRedirectFromMfa = (redirectParam?: string): string => {
  const validatedRedirect = validateRedirect(redirectParam, PATIENT_REDIRECT_WHITELIST);
  if (!validatedRedirect || validatedRedirect.startsWith('/mfa-setup')) return '/';
  return validatedRedirect;
};

const getMfaRedirect = ({
  mfaIsComplete,
  hasPhoneNumber,
  hasTrustReferral,
  isMfaSetupRoute,
  pathname,
  redirectParam,
}: {
  mfaIsComplete: boolean;
  hasPhoneNumber: boolean;
  hasTrustReferral: boolean;
  isMfaSetupRoute: boolean;
  pathname: string;
  redirectParam?: string;
}) => {
  if (!hasPhoneNumber && !hasTrustReferral) return null;
  if (mfaIsComplete && isMfaSetupRoute) return getRedirectFromMfa(redirectParam);
  if (!mfaIsComplete && !isMfaSetupRoute) return getSetupUrl(hasPhoneNumber, pathname);
  return null;
};

const useMfaBlockingCheck = ({
  fetchingServiceUser,
  hasPhoneNumber,
  hasResolved,
  isAuthenticated,
  isLoadingAuth,
  mfaIsComplete,
  isLoadingMfaStatus: loading,
}: Omit<MfaStatus, 'error'> & {
  fetchingServiceUser: boolean;
  isAuthenticated: boolean;
  isLoadingAuth: boolean;
}) =>
  useMemo(
    () =>
      isLoadingAuth ||
      (!hasResolved && loading) ||
      mfaIsComplete === null ||
      hasPhoneNumber === null ||
      fetchingServiceUser,
    [fetchingServiceUser, hasPhoneNumber, hasResolved, isLoadingAuth, mfaIsComplete, loading]
  );

const hasResolvedMfaStatus = (
  mfaStatus: MfaStatus
): mfaStatus is MfaStatus & { hasPhoneNumber: boolean; mfaIsComplete: boolean } =>
  mfaStatus.hasPhoneNumber !== null && mfaStatus.mfaIsComplete !== null;

const RequireMfaSetup = () => {
  const { apiUrl, isAuthenticated, isLoading: isLoadingAuth } = useAuthentication();
  const { pathname } = useLocation();
  const redirectParam = useQueryParam('redirect');
  const isMfaSetupRoute = pathname === '/mfa-setup';
  const mfaStatus = useMfaStatus({
    apiUrl,
    isAuthenticated,
    isLoadingAuth,
    isMfaSetupRoute,
  });
  const [serviceUser, fetchingServiceUser] = useGetServiceUserQuery();
  const hasTrustReferral = serviceUser?.hasTrustReferral ?? false;
  const isGuardBlocked = useMfaBlockingCheck({
    ...mfaStatus,
    fetchingServiceUser,
    isAuthenticated,
    isLoadingAuth,
  });
  const redirectTo = useMemo(() => {
    if (mfaStatus.error) return null;
    if (!hasResolvedMfaStatus(mfaStatus)) return null;
    if (isGuardBlocked) return null;

    return getMfaRedirect({
      hasPhoneNumber: mfaStatus.hasPhoneNumber,
      hasTrustReferral,
      isMfaSetupRoute,
      mfaIsComplete: mfaStatus.mfaIsComplete,
      pathname,
      redirectParam,
    });
  }, [mfaStatus, isGuardBlocked, hasTrustReferral, isMfaSetupRoute, pathname, redirectParam]);

  if (!isLoadingAuth && !isAuthenticated) throw new Error('not authenticated');
  if (mfaStatus.error) throw mfaStatus.error;
  if (isGuardBlocked) return null;
  if (redirectTo) {
    return <Navigate replace to={redirectTo} />;
  }
  return <Outlet />;
};

export { RequireMfaSetup };
